/**
 * Central test/live configuration for the Gatsby website.
 *
 * Set in `.env.production` (build), `.env.development` (local dev), or CI/Netlify env vars:
 *   GATSBY_DUFFLOO_MODE=test   → test Firestore collections + dev backend + Stripe test key
 *   GATSBY_DUFFLOO_MODE=live   → production (default)
 *
 * Optional overrides:
 *   GATSBY_BACKEND_BASE_URL=https://...   → also wins over the browser
 *     same-origin /api/v1 proxy (see getBackendBaseUrl)
 *
 * Firebase web config (see FIREBASE_CONFIG):
 *   GATSBY_FIREBASE_API_KEY, GATSBY_FIREBASE_AUTH_DOMAIN, GATSBY_FIREBASE_DATABASE_URL,
 *   GATSBY_FIREBASE_PROJECT_ID, GATSBY_FIREBASE_STORAGE_BUCKET,
 *   GATSBY_FIREBASE_MESSAGING_SENDER_ID, GATSBY_FIREBASE_APP_ID, GATSBY_FIREBASE_MEASUREMENT_ID
 *   authDomain must match deploy hostname (www.duffloo.com prod, localhost:8000 dev).
 */

const normalizeMode = (value) => {
  const mode = String(value || "live").trim().toLowerCase();
  return mode === "test" ? "test" : "live";
};

export const DUFFLOO_MODE = normalizeMode(process.env.GATSBY_DUFFLOO_MODE);
export const IS_TEST_MODE = DUFFLOO_MODE === "test";

/** @deprecated Prefer DUFFLOO_MODE — kept for existing washer onboarding imports */
export const WASHER_DATA_MODE = DUFFLOO_MODE;

export const FIRESTORE_COLLECTIONS = {
  accounts: IS_TEST_MODE ? "TestAccounts" : "Accounts",
  washers: IS_TEST_MODE ? "test_washers" : "washers",
  users: IS_TEST_MODE ? "test_users" : "users",
  requests: IS_TEST_MODE ? "test_requests" : "requests",
  washerContracts: IS_TEST_MODE ? "test_washer_contracts" : "washer_contracts",
  // W-02: must match the Hero app's FIREBASE_{TEST,LIVE}_SUPPORT_CHATS exactly
  // — the app and this site are the same conversation, so a mismatched name
  // would silently fork the thread in two.
  supportChats: IS_TEST_MODE ? "test_support_chats" : "support_chats",
};

/** Firebase Storage path prefix for washer uploads (contracts, signatures, etc.) */
export const FIRESTORE_STORAGE_BASE = IS_TEST_MODE ? "test" : "live";

const BACKEND_URLS = {
  // live: "https://duffloo-backend.herokuapp.com/v1",
  live: "https://duffloo-backend-development-53c25cb68321.herokuapp.com/v1",
  test: "https://duffloo-backend-development-53c25cb68321.herokuapp.com/v1",
};

const BACKEND_BASE_URL_OVERRIDE = String(
  process.env.GATSBY_BACKEND_BASE_URL || ""
).trim();

const DEFAULT_BACKEND_BASE_URL =
  BACKEND_BASE_URL_OVERRIDE ||
  BACKEND_URLS[DUFFLOO_MODE] ||
  BACKEND_URLS.live;

/** Heroku direct URL (native apps, server-side). */
export const BACKEND_BASE_URL = DEFAULT_BACKEND_BASE_URL;

/**
 * Browser base URL — on Netlify/local use same-origin proxy (/api/v1) so
 * washer handoff exchange is not blocked by Heroku CORS allowlist.
 *
 * An explicitly set GATSBY_BACKEND_BASE_URL wins over BOTH branches: there is
 * no gatsby-develop proxy for /api/v1, so `gatsby develop` against a backend on
 * another port (or against the Firestore/Auth emulator suite) needs to address
 * that origin directly — otherwise every call 404s on the Gatsby dev server.
 */
export const getBackendBaseUrl = () => {
  if (BACKEND_BASE_URL_OVERRIDE) {
    return BACKEND_BASE_URL_OVERRIDE;
  }
  if (typeof window === "undefined") {
    return DEFAULT_BACKEND_BASE_URL;
  }
  const host = window.location.hostname || "";
  if (host.endsWith(".netlify.app") || host === "localhost") {
    return `${window.location.origin}/api/v1`;
  }
  return DEFAULT_BACKEND_BASE_URL;
};

const STRIPE_TEST_PUBLISHABLE_KEY =
  process.env.GATSBY_STRIPE_TEST_PUBLISHABLE_KEY ||
  process.env.GATSBY_STRIPE_TEST_PUBLISH_KEY ||
  "";

const STRIPE_LIVE_PUBLISHABLE_KEY =
  process.env.GATSBY_STRIPE_LIVE_PUBLISHABLE_KEY ||
  process.env.GATSBY_STRIPE_LIVE_PUBLISH_KEY ||
  "";

export const getStripePublishableKey = () => {
  const key = IS_TEST_MODE
    ? STRIPE_TEST_PUBLISHABLE_KEY
    : STRIPE_LIVE_PUBLISHABLE_KEY;

  if (!key) {
    console.warn(
      `[Duffloo] Missing Stripe ${IS_TEST_MODE ? "test" : "live"} publishable key in env`
    );
  }

  return key;
};

const envString = (value) => String(value || "").trim();

export const FIREBASE_CONFIG = {
  // Gatsby inlines process.env.GATSBY_* only for literal property access — not process.env[key].
  apiKey: envString(process.env.GATSBY_FIREBASE_API_KEY),
  authDomain: envString(process.env.GATSBY_FIREBASE_AUTH_DOMAIN),
  databaseURL: envString(process.env.GATSBY_FIREBASE_DATABASE_URL),
  projectId: envString(process.env.GATSBY_FIREBASE_PROJECT_ID),
  storageBucket: envString(process.env.GATSBY_FIREBASE_STORAGE_BUCKET),
  messagingSenderId: envString(process.env.GATSBY_FIREBASE_MESSAGING_SENDER_ID),
  appId: envString(process.env.GATSBY_FIREBASE_APP_ID),
  measurementId: envString(process.env.GATSBY_FIREBASE_MEASUREMENT_ID),
};

const REQUIRED_FIREBASE_ENV = [
  ["apiKey", "GATSBY_FIREBASE_API_KEY"],
  ["authDomain", "GATSBY_FIREBASE_AUTH_DOMAIN"],
  ["projectId", "GATSBY_FIREBASE_PROJECT_ID"],
  ["appId", "GATSBY_FIREBASE_APP_ID"],
  ["storageBucket", "GATSBY_FIREBASE_STORAGE_BUCKET"],
];

for (const [field, envKey] of REQUIRED_FIREBASE_ENV) {
  if (!FIREBASE_CONFIG[field]) {
    console.warn(
      `[Duffloo] Missing ${envKey} — set in .env.production (build) or .env.development (dev), then restart`
    );
  }
}
